import React, {
  createContext,
  useContext,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import type { SeitenTitelDaten } from "../components/layout/SeitenTitelContext";

/**
 * Ein Projekt, auf das die Startseite gerade eingeschränkt ist. Der Header zeigt es als
 * Unterebene hinter „strømcraft“ – nicht als Titel wie im Projekt, denn man ist noch auf der
 * Startseite und nicht im Projekt.
 */
export interface HeaderFocus {
  label: string;
  /** Öffnet das Projekt */
  onOpen: () => void;
  /** Hebt die Einschränkung auf, zurück zu allen Windparks */
  onClear: () => void;
}

/** Ein Projekt, das der Nutzer über das Einstellungsmenü teilen darf */
export interface ShareableProject {
  id: string;
  name: string;
}

interface HeaderContextType {
  pageTitle: string | null;
  /** Überschrift der aktuellen Seite – der Header zeigt sie über der Inhaltsspalte */
  pageHeading: SeitenTitelDaten | null;
  /**
   * Das Projekt für „Projekt teilen“ im Einstellungsmenü, sonst null.
   *
   * Über den Context, weil der Header global gerendert wird: Nur das Projekt-Layout weiß, ob
   * es ein Projekt gibt und ob der Nutzer zum Mandanten gehört.
   */
  shareProject: ShareableProject | null;
  focus: HeaderFocus | null;
  setPageTitle: (title: string | null) => void;
  setPageHeading: (heading: SeitenTitelDaten | null) => void;
  setShareProject: (project: ShareableProject | null) => void;
  setFocus: (focus: HeaderFocus | null) => void;
}

const HeaderContext = createContext<HeaderContextType | undefined>(undefined);

export const HeaderProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [pageTitle, setPageTitle] = useState<string | null>(null);
  const [pageHeading, setPageHeading] = useState<SeitenTitelDaten | null>(
    null,
  );
  const [shareProject, setShareProject] = useState<ShareableProject | null>(
    null,
  );
  const [focus, setFocus] = useState<HeaderFocus | null>(null);

  return (
    <HeaderContext.Provider
      value={{
        pageTitle,
        pageHeading,
        shareProject,
        focus,
        setPageTitle,
        setPageHeading,
        setShareProject,
        setFocus,
      }}
    >
      {children}
    </HeaderContext.Provider>
  );
};

export const useHeader = (): HeaderContextType => {
  const ctx = useContext(HeaderContext);
  if (!ctx)
    throw new Error(
      "useHeader muss innerhalb von HeaderProvider verwendet werden",
    );
  return ctx;
};

/**
 * Hook zum Setzen des Titels im globalen Header (statt des Projektnamens).
 * Wird beim Unmounten automatisch zurückgesetzt. Einen Zurück-Pfeil gibt es dort nicht: Zur
 * Startseite führt das Logo.
 */
export const useHeaderConfig = (title: string | null) => {
  const { setPageTitle } = useHeader();

  React.useEffect(() => {
    setPageTitle(title);
    return () => setPageTitle(null);
  }, [title, setPageTitle]);
};

/**
 * Bringt die Überschrift der Seite in den Header und räumt sie beim Verlassen wieder ab.
 *
 * Neu gesetzt wird nur, wenn sich Titel oder Rückweg ändern – nicht bei jedem Rendern der
 * Seite, sonst rendert der Header bei jedem Tastendruck in einem Formular mit. Der Rückweg
 * ruft immer den aktuellen Handler der Seite.
 */
export const useHeaderPageHeading = (heading: SeitenTitelDaten | null) => {
  const { setPageHeading } = useHeader();
  const onBack = useRef(heading?.back?.onClick);
  onBack.current = heading?.back?.onClick;

  const titel = heading?.titel;
  const backLabel = heading?.back?.label;

  useLayoutEffect(() => {
    if (!titel) return;
    setPageHeading({
      titel,
      back: backLabel
        ? { label: backLabel, onClick: () => onBack.current?.() }
        : undefined,
    });
    return () => setPageHeading(null);
  }, [titel, backLabel, setPageHeading]);
};

/**
 * Bietet ein Projekt zum Teilen im Einstellungsmenü an, solange der Aufrufer es setzt.
 */
export const useHeaderShareProject = (project: ShareableProject | null) => {
  const { setShareProject } = useHeader();
  const id = project?.id;
  const name = project?.name;

  React.useEffect(() => {
    if (!id || !name) return;
    setShareProject({ id, name });
    return () => setShareProject(null);
  }, [id, name, setShareProject]);
};

/**
 * Zeigt das Projekt, auf das die Seite eingeschränkt ist, im Header und räumt es beim
 * Verlassen wieder ab.
 */
export const useHeaderFocus = (
  focus: HeaderFocus | null,
  abhaengigkeiten: unknown[],
) => {
  const { setFocus } = useHeader();

  React.useEffect(() => {
    setFocus(focus);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, abhaengigkeiten);

  React.useEffect(() => () => setFocus(null), [setFocus]);
};
